import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Users, Truck, AlertTriangle, Calendar, Gift, ChevronLeft, ChevronRight } from 'lucide-react';

// Direct image imports to ensure proper bundling
import carouselImage1 from '/assets/images/domov/pgd-zp-carousal1.jpg';
import carouselImage2 from '/assets/images/domov/pgd-zp-carousal2.jpg';
import carouselImage3 from '/assets/images/domov/pgd-zp-carousal3.jpg';

const carouselImages = [carouselImage1, carouselImage2, carouselImage3];

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Auto-slide effect with setInterval for consistent timing
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
        setIsTransitioning(false);
      }, 500); // Transition duration
    }, 5000); // Slide duration

    return () => clearInterval(interval); // Cleanup on unmount
  }, []);

  // Navigation handlers
  const goToPrevSlide = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev === 0 ? carouselImages.length - 1 : prev - 1));
      setIsTransitioning(false);
    }, 500);
  };

  const goToNextSlide = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
      setIsTransitioning(false);
    }, 500);
  };

  const goToSlide = (index: number) => {
    if (index !== currentSlide) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentSlide(index);
        setIsTransitioning(false);
      }, 500);
    }
  };

  return (
    <>
      {/* Hero Carousel Section */}
      <section className="relative h-[80vh] overflow-hidden">
        {/* Slides */}
        {carouselImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-500 ${
              index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
            } ${isTransitioning ? 'transition-opacity' : ''}`}
          >
            <img
              src={image}
              alt={`Slide ${index + 1}`}
              className="w-full h-full object-cover object-center"
              loading="eager" // Load images immediately
              onError={() => console.error(`Failed to load image ${index + 1}`)} // Log loading errors
            />
          </div>
        ))}

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/60 z-20"></div>

        {/* Content */}
        <div className="relative h-full flex items-center justify-center z-30">
          <div className="text-center max-w-4xl mx-auto px-4 py-16">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight text-white">
              Prostovoljno gasilsko društvo Zgornja Polskava
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-white">
              Z znanjem in pogumom v službi skupnosti že več kot 100 let
            </p>
            <Link to="/o-nas" className="btn-primary group">
              Spoznaj nas
              <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        {/* Navigation Buttons */}
        <button
          className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 transition-colors"
          onClick={goToPrevSlide}
          aria-label="Previous slide"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 transition-colors"
          onClick={goToNextSlide}
          aria-label="Next slide"
        >
          <ChevronRight className="h-6 w-6" />
        </button>

        {/* Indicators */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex space-x-2">
          {carouselImages.map((_, index) => (
            <button
              key={index}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide ? 'bg-primary w-6' : 'bg-white/50 hover:bg-white/80'
              }`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            ></button>
          ))}
        </div>
      </section>

      {/* Mission Section */}
      <section className="page-section bg-gray-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="section-title text-secondary">Naše poslanstvo</h2>
            <p className="mt-8 text-lg text-gray-700">
              PGD Zgornja Polskava si prizadeva zagotavljati vrhunsko požarno varnost, 
              nuditi pomoč v nujnih primerih in aktivno prispevati k varnosti naše skupnosti 
              z nenehnim izobraževanjem in usposabljanjem.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg hover:shadow-primary/20 border border-gray-200 hover:border-primary transition-all duration-300">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Flame className="text-primary h-7 w-7" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Požarna varnost</h3>
              <p className="text-gray-600">
                Zagotavljamo hitro in učinkovito gašenje požarov ter preventivno delujemo na področju požarne varnosti.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg hover:shadow-primary/20 border border-gray-200 hover:border-primary transition-all duration-300">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="text-primary h-7 w-7" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Reševanje</h3>
              <p className="text-gray-600">
                Izvajamo reševalne operacije v primeru nesreč, poplav in drugih naravnih ali tehničnih nesreč.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg hover:shadow-primary/20 border border-gray-200 hover:border-primary transition-all duration-300">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Users className="text-primary h-7 w-7" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Skupnost</h3>
              <p className="text-gray-600">
                Krepimo povezanost lokalne skupnosti s pomočjo preventivnih aktivnosti in kulturnih dogodkov.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="page-section">
        <div className="container-custom">
          <h2 className="section-title text-center mb-12 text-secondary">Hitre povezave</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to="/oprema" className="group">
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-md hover:shadow-lg hover:shadow-primary/20 group-hover:border-primary transition-all duration-300">
                <div className="p-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <Truck className="text-primary h-8 w-8" />
                    <h3 className="text-xl font-semibold">Oprema</h3>
                  </div>
                  <p className="text-gray-600">
                    Spoznajte našo opremo in vozila, s katerimi zagotavljamo varnost.
                  </p>
                </div>
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-primary font-medium group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                  Več o opremi
                </div>
              </div>
            </Link>

            <Link to="/intervencije" className="group">
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-md hover:shadow-lg hover:shadow-primary/20 group-hover:border-primary transition-all duration-300">
                <div className="p-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <AlertTriangle className="text-primary h-8 w-8" />
                    <h3 className="text-xl font-semibold">Intervencije</h3>
                  </div>
                  <p className="text-gray-600">
                    Pregled nedavnih intervencij in reševalnih akcij našega društva.
                  </p>
                </div>
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-primary font-medium group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                  Preglej intervencije
                </div>
              </div>
            </Link>

            <Link to="/donacija" className="group">
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-md hover:shadow-lg hover:shadow-primary/20 group-hover:border-primary transition-all duration-300">
                <div className="p-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <Gift className="text-primary h-8 w-8" />
                    <h3 className="text-xl font-semibold">Donacija</h3>
                  </div>
                  <p className="text-gray-600">
                    Podprite naše delo z donacijo dela dohodnine in prispevajte k varnosti.
                  </p>
                </div>
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-primary font-medium group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                  Kako donirati?
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary text-white py-16">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Postani del naše ekipe</h2>
          <p className="text-lg max-w-3xl mx-auto mb-8">
            Vabimo vse, ki bi radi pomagali skupnosti in razvijali svoje sposobnosti, 
            da se pridružite našemu gasilskemu društvu. Skupaj lahko naredimo razliko!
          </p>
          <Link to="/kontakt" className="bg-white text-primary hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg inline-flex items-center transition-colors">
            Pridruži se nam
          </Link>
        </div>
      </section>

      {/* Recent Events/News Preview */}
      <section className="page-section bg-gray-50">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-12">
            <h2 className="section-title text-secondary">Zadnje novice in dogodki</h2>
            <Link to="/intervencije" className="text-primary hover:text-primary-700 font-medium flex items-center mt-4 md:mt-0">
              Vse novice in dogodki
              <span className="ml-2">→</span>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-md hover:shadow-lg hover:shadow-primary/20 border border-gray-200 hover:border-primary transition-all duration-300 p-6">
              <div className="flex items-center text-gray-500 text-sm mb-3">
                <Calendar className="h-4 w-4 mr-1" />
                <span>15. marec 2025</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">
                <Link to="/intervencije" className="text-primary hover:text-primary-700 transition-colors">
                  Uspešna gasilska vaja na osnovni šoli
                </Link>
              </h3>
              <p className="text-gray-600 mb-4">
                Izvedli smo uspešno gasilsko vajo evakuacije v sodelovanju z Osnovno šolo Zgornja Polskava.
              </p>
              <Link to="/intervencije" className="text-primary hover:text-primary-700 font-medium inline-flex items-center">
                Preberi več
                <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </div>

            <div className="bg-white rounded-xl shadow-md hover:shadow-lg hover:shadow-primary/20 border border-gray-200 hover:border-primary transition-all duration-300 p-6">
              <div className="flex items-center text-gray-500 text-sm mb-3">
                <Calendar className="h-4 w-4 mr-1" />
                <span>28. februar 2025</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">
                <Link to="/intervencije" className="text-primary hover:text-primary-700 transition-colors">
                  Pridobitev nove gasilske opreme
                </Link>
              </h3>
              <p className="text-gray-600 mb-4">
                Naše društvo je prejelo novo opremo za tehnično reševanje, ki bo pripomogla k še učinkovitejšemu delu.
              </p>
              <Link to="/intervencije" className="text-primary hover:text-primary-700 font-medium inline-flex items-center">
                Preberi več
                <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </div>

            <div className="bg-white rounded-xl shadow-md hover:shadow-lg hover:shadow-primary/20 border border-gray-200 hover:border-primary transition-all duration-300 p-6">
              <div className="flex items-center text-gray-500 text-sm mb-3">
                <Calendar className="h-4 w-4 mr-1" />
                <span>5. januar 2025</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">
                <Link to="/intervencije" className="text-primary hover:text-primary-700 transition-colors">
                  Intervencija ob požaru gospodarskega poslopja
                </Link>
              </h3>
              <p className="text-gray-600 mb-4">
                Hitro posredovanje gasilcev je preprečilo širjenje požara na bližnje objekte.
              </p>
              <Link to="/intervencije" className="text-primary hover:text-primary-700 font-medium inline-flex items-center">
                Preberi več
                <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;